import { useState } from "react";
import {
  EventPositionDTO,
  UserPublicInfoDto,
} from "@/pages/admin/events/types";
import { Button } from "@/components/ui/button";
import { PositionSelector } from "../position-selector/PositionSelector";
import { EmployeeSelector } from "../employee-selector/EmployeeSelector";
import { Loading } from "@/components/ui/loading";
import { useEmployeesList } from "@/hooks/useEmployeesList";
import { useToast } from "@/hooks/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface AssignDialogContentProps {
  eventId: string;
  eventPositions: EventPositionDTO[];
  onEmployeeAssigned: () => void;
  onClose: () => void;
}

export function AssignDialogContent({
  eventId,
  eventPositions,
  onEmployeeAssigned,
  onClose,
}: AssignDialogContentProps) {
  const { toast } = useToast();
  const [selectedEmployee, setSelectedEmployee] =
    useState<UserPublicInfoDto | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);

  const { data: employees, isLoading } = useEmployeesList(true);

  // Ensure employees is always an array
  const safeEmployees = Array.isArray(employees) ? employees : [];

  const selectedPosition = eventPositions.find(
    (p) => p.eventPositionId === selectedPositionId,
  );

  const handleAssign = async () => {
    if (!selectedEmployee || !selectedPositionId) {
      toast({
        title: "Error",
        description: "Please select both a position and an employee",
        variant: "destructive",
      });
      return;
    }

    setIsAssigning(true);
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_API_URL}event/position/${selectedPositionId}/assign/${selectedEmployee.id}`,
        {
          employeeId: selectedEmployee.id,
        },
      );

      toast({
        title: "Success",
        description: `${selectedEmployee.firstName} ${selectedEmployee.lastName} was assigned to ${selectedPosition?.tableName}`,
        variant: "success",
      });

      onClose();
      setSelectedEmployee(null);
      setSelectedPositionId("");
      onEmployeeAssigned();
    } catch (error) {
      console.error("Error assigning employee:", error);
      toast({
        title: "Error",
        description: `Failed to assign employee. ${error.response.data.error}`,
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Assign Employee</DialogTitle>
      </DialogHeader>

      <div className="py-4 space-y-4">
        <PositionSelector
          eventPositions={eventPositions}
          selectedPositionId={selectedPositionId}
          onSelectPosition={setSelectedPositionId}
        />

        <EmployeeSelector
          employees={safeEmployees}
          isLoading={isLoading}
          selectedEmployee={selectedEmployee}
          onSelectEmployee={setSelectedEmployee}
          disabled={!selectedPositionId}
        />
      </div>

      <DialogFooter>
        <Button
          onClick={handleAssign}
          disabled={!selectedEmployee || !selectedPositionId || isAssigning}
          className="gap-2"
        >
          {isAssigning && <Loading size="sm" className="mr-2" />}
          Assign Employee
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
